import React from "react";
import { MapStateToPropsParam } from "react-redux";

import reducer, { ReviewsState } from "@/mk2/apps/bazaar/containers/Reviews/Reviews.reducers";
import saga from "@/mk2/apps/bazaar/containers/Reviews/Reviews.sagas";
import { getReviewsSellerState } from "@/mk2/apps/bazaar/containers/Reviews/Reviews.selectors";
import SellerReviews from "@/mk2/apps/bazaar/containers/SellerReviews/SellerReviews";
import {
    ReviewEntity,
    ReviewRole,
    ReviewSchema,
    SellerEntity,
    SellerSchema,
} from "@/mk2/apps/bazaar/schemas";
import { bazaarSellerReviewsFromBuyersUrl } from "@/mk2/apps/bazaar/urls";
import { AppName } from "@/mk2/containers/Application/Application";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, { PageRouteProps } from "@/mk2/pages/Page";
import { AppState, getRequestBaseUrl, getRequestPermissions, getRequestUser } from "@/mk2/reducers";
import { MenuSelection, UserEntity } from "@/mk2/schemas";
import { findDenormalizedEntityByCond, getDenormalizedEntities } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import { BAZAAR_SEO_BABY, REVIEWS_OF_THE_SELLER_username } from "@/mk/autogenerated/translations/SellerReviewsPage.1acf2e10709b5c43ccda10ff3ee3bd09";

export interface OwnProps {}

export interface StateProps {
    baseUrl: string;
    seller: SellerEntity;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    reviews: ReviewEntity[];
}

export interface DispatchProps {}

export type RouteProps = PageRouteProps<{
    username: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class SellerReviewsPage extends Page<OwnProps, StateProps, DispatchProps> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                routeParams: { username },
            } = ownProps;

            const currentSellerState = getReviewsSellerState(state, username);
            const role =
                ownProps.routePathPattern === bazaarSellerReviewsFromBuyersUrl
                    ? ReviewRole.SELLER
                    : ReviewRole.BUYER;
            const currentReviewsState: ReviewsState = currentSellerState[role];

            return {
                // Seller can be already loaded from different page - search by username
                baseUrl: getRequestBaseUrl(state),
                seller: findDenormalizedEntityByCond(
                    state,
                    SellerSchema,
                    `cacheKey-SellerReviewsPage-${username}`,
                    (seller) => seller.username === username,
                ),
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
                reviews: getDenormalizedEntities<ReviewEntity>(
                    state,
                    ReviewSchema,
                    currentReviewsState.ids,
                ),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {};
    }

    public getMetaTitle(props: Props): string {
        return (
            interpolate(REVIEWS_OF_THE_SELLER_username, {
                username: props.routeParams.username,
            }) +
            " | " +
            BAZAAR_SEO_BABY
        );
    }

    public render(props: Props): false | JSX.Element {
        return <SellerReviews {...props} />;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: app,
            activeItem: null,
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.bazaar.reviews", reducer);
        store.injectSaga("containers.bazaar.reviews", saga);
    }
}
